/**
 * Import d'un export **Delinea Secret Server** XML (ex-Thycotic : « Admin >
 * Export / Import > Export » au format XML ; ses utilisateurs doivent pouvoir
 * partir avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<ImportFile>` porte `Folders > Folder` (les
 * dossiers) et `Secrets > Secret` : `SecretName`, `FolderPath` (`\A\B`),
 * `TotpKey`, `TotpBackupCodes` et `SecretItems > SecretItem` (`FieldName` =
 * nom affiche, `Slug` = nom technique, `Value`). Lecture d'un secret :
 * `delineaSecretEntry`, commune avec l'export CSV.
 */
import { delineaSecretEntry, folderPathOf, type DelineaSecretItem, type ImportedEntry } from './csv-import.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';

/** Le noeud `<ImportFile>` d'un export Delinea, ou `null` si le texte n'en est pas un. */
function delineaImportFile(text: string): XmlNode | null {
  if (!/^[\s﻿]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<ImportFile[\s/>]/u.test(text)) return null;
  let document: XmlNode;
  try {
    document = parseXml(text);
  } catch {
    return null;
  }
  const importFile = childNamed(document, 'ImportFile');
  return importFile !== null && childNamed(importFile, 'Secrets') !== null ? importFile : null;
}

function textOf(node: XmlNode | null, tag: string): string {
  return childNamed(node, tag)?.text ?? '';
}

/** Champs d'un secret : ceux du modele (`SecretItems`), puis sa graine TOTP et ses codes de secours. */
function secretItems(secret: XmlNode): DelineaSecretItem[] {
  const items = childrenNamed(childNamed(secret, 'SecretItems'), 'SecretItem').map((item) => {
    const slug = textOf(item, 'Slug').trim().toLowerCase();
    return { slug, label: textOf(item, 'FieldName').trim() || slug, value: textOf(item, 'Value') };
  });
  return [
    ...items.filter((item) => item.slug.length > 0),
    { slug: 'totp-key', label: 'TOTP Key', value: textOf(secret, 'TotpKey') },
    { slug: 'totp-backup-codes', label: 'TOTP Backup Codes', value: textOf(secret, 'TotpBackupCodes') },
  ];
}

/** Un export Delinea se reconnait a son document `<ImportFile>` portant `Secrets`. */
export function isDelineaXml(text: string): boolean {
  return delineaImportFile(text) !== null;
}

/** Parse un export Delinea Secret Server XML : un secret = une entree, rangee dans son dossier. */
export function parseDelineaXml(text: string): ImportedEntry[] {
  return readDelineaXml(text).entries;
}

/** Secrets ET dossiers de `Folders > Folder`, VIDES compris (`FolderPath` `\A\B` : imbrique). */
export function readDelineaXml(text: string): { entries: ImportedEntry[]; folders: (readonly string[])[] } {
  const importFile = delineaImportFile(text);
  if (importFile === null) throw new Error('export Delinea : structure inattendue');
  const entries: ImportedEntry[] = [];
  for (const secret of childrenNamed(childNamed(importFile, 'Secrets'), 'Secret')) {
    const entry = delineaSecretEntry({ name: textOf(secret, 'SecretName'), folder: textOf(secret, 'FolderPath'), items: secretItems(secret) });
    if (entry !== null) entries.push(entry);
  }
  const folders = childrenNamed(childNamed(importFile, 'Folders'), 'Folder')
    .map((folder) => folderPathOf(textOf(folder, 'FolderPath') || textOf(folder, 'FolderName')).folderPath)
    .filter((path) => path !== undefined);
  return { entries, folders };
}
